import React, { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useNotification, NotificationType } from "./NotificationProvider";
import { Session } from "@/lib/curio";
import { LoginCredentials } from "@/types/auth";
import { useAuthQuery } from "@/hooks/useAuthQuery";

interface AuthContextType {
  session?: Session;
  isLoading: boolean;
  isAuth: boolean;
  login: (params: LoginCredentials) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser utilizado dentro de um AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { notification } = useNotification();
  const navigate = useNavigate();

  const { session, isLoading, isAuth, login: authLogin, logout: authLogout } = useAuthQuery();

  const logout = useCallback(() => {
    authLogout();
    navigate("/", { replace: true });
  }, [authLogout, navigate]);

  // Fonte única de logout: as mensagens de erro são exibidas pelo
  // NotificationProvider (via queryClient.onError) e aqui reagimos apenas às
  // que sinalizam erro de autenticação (notification.isAuthError) ou sessão
  // expirada. O ref evita disparar logout mais de uma vez para a mesma
  // notificação — `logout` muda de identidade a cada render.
  const handledNotificationRef = useRef<NotificationType | null>(null);

  useEffect(() => {
    if (!notification) {
      return;
    }
    const expiredSession = notification.message === "Sessão expirada!";
    if (!notification.isAuthError && !expiredSession) {
      return;
    }
    if (handledNotificationRef.current === notification) {
      return;
    }
    handledNotificationRef.current = notification;
    logout();
  }, [notification, logout]);

  const login = async (params: LoginCredentials) => {
    await authLogin(params);
    navigate("/inicio");
  };

  return (
    <AuthContext.Provider
      value={{
        isAuth,
        session,
        isLoading,
        login,
        logout
      }}>
      {children}
    </AuthContext.Provider>
  );
};
